import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle2,
  FileSignature,
  Calendar,
  Clock,
  User,
  AlertCircle,
  Phone,
  Trash2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Caregiver Callout Portal
 * 
 * Allows caregivers to report a callout with:
 * 1. Policy reminder screen
 * 2. E-signature acknowledgment
 * 3. Callout details form
 */

const CALLOUT_REASONS = [
  { value: 'illness', label: 'Illness', description: 'Sick, medical appointment' },
  { value: 'emergency', label: 'Emergency', description: 'Urgent unexpected situation' },
  { value: 'family', label: 'Family Matter', description: 'Family emergency or obligation' },
  { value: 'personal', label: 'Personal', description: 'Personal matter' },
  { value: 'other', label: 'Other', description: 'Other reason' },
];

export default function CaregiverCallout() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1); // 1: Policy, 2: Signature, 3: Details
  
  // Policy data
  const [policy, setPolicy] = useState(null);
  
  // Signature state
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signatureMode, setSignatureMode] = useState('draw');
  const [typedName, setTypedName] = useState('');
  const [policyAcknowledged, setPolicyAcknowledged] = useState(false);
  
  // Callout form data
  const [formData, setFormData] = useState({
    shift_date: '',
    shift_start_time: '',
    shift_end_time: '',
    client_name: '',
    reason: '',
    reason_details: '',
    expected_return_date: '',
  });

  useEffect(() => {
    fetchPolicy();
  }, []);

  useEffect(() => {
    if (canvasRef.current && signatureMode === 'draw' && step === 2) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#0D1B33';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  }, [signatureMode, step]);

  const fetchPolicy = async () => {
    try {
      const res = await fetch(`${API_URL}/api/coverage/policies/callout`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setPolicy(data);
      }
    } catch (err) {
      console.error('Error fetching policy:', err);
    } finally {
      setLoading(false);
    }
  };

  // Canvas drawing handlers
  const startDrawing = (e) => {
    if (signatureMode !== 'draw') return;
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || signatureMode !== 'draw') return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    if (signatureMode === 'draw' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      setHasSignature(false);
    } else {
      setTypedName('');
    }
  };

  const getSignatureData = () => {
    if (signatureMode === 'draw' && canvasRef.current) {
      return canvasRef.current.toDataURL('image/png');
    } else if (signatureMode === 'type' && typedName) {
      const canvas = document.createElement('canvas');
      canvas.width = 400;
      canvas.height = 100;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = 'italic 32px "Brush Script MT", cursive';
      ctx.fillStyle = '#0D1B33';
      ctx.textAlign = 'center';
      ctx.fillText(typedName, 200, 60);
      return canvas.toDataURL('image/png');
    }
    return null;
  };

  // Success screen state
  const [submitted, setSubmitted] = useState(false);
  const [calloutId, setCalloutId] = useState(null);

  const handleSubmit = async () => {
    if (!formData.shift_date) {
      toast.error('Please select the shift date');
      return;
    }
    if (!formData.reason) {
      toast.error('Please select a reason');
      return;
    }

    setSubmitting(true);
    try {
      const signatureData = getSignatureData();
      
      const res = await fetch(`${API_URL}/api/coverage/callouts`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          signature_data: signatureData,
          signature_method: signatureMode,
          policy_acknowledged: policyAcknowledged,
        })
      });

      if (res.ok) {
        const data = await res.json();
        setCalloutId(data.id || data.callout_id);
        setSubmitted(true);
        // Don't navigate away - show call confirmation screen
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit callout');
      }
    } catch (err) {
      console.error('Error submitting callout:', err);
      toast.error('Failed to submit callout');
    } finally {
      setSubmitting(false);
    }
  };

  const OFFICE_PHONE = '(559) 795-5455';
  const OFFICE_PHONE_RAW = '5597955455';

  const canProceedFromStep1 = true; // Just read the policy
  const canProceedFromStep2 = policyAcknowledged && (
    (signatureMode === 'draw' && hasSignature) ||
    (signatureMode === 'type' && typedName.trim().length > 0)
  );

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBFBFA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#176DFF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBFBFA]">
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Header */}
        <header className="mb-6">
          <button
            onClick={() => navigate('/my-leynix')}
            className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0D1B33] mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to My Leynix
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-red-500 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-[#0D1B33]">Report a Callout</h1>
              <p className="text-sm text-[#64748B]">Unable to work a scheduled shift</p>
            </div>
          </div>
        </header>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  s < step
                    ? 'bg-emerald-500 text-white'
                    : s === step
                    ? 'bg-[#176DFF] text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {s < step ? <CheckCircle2 className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-12 h-1 rounded ${s < step ? 'bg-emerald-300' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Policy Reminder */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="bg-amber-50 rounded-xl border-2 border-amber-200 p-6">
              <div className="flex items-start gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-amber-600 mt-0.5" />
                <div>
                  <h2 className="text-lg font-semibold text-amber-900">
                    {policy?.title || 'ATOA Call-Out Policy Reminder'}
                  </h2>
                  <p className="text-sm text-amber-700 mt-1">
                    Please read carefully before proceeding
                  </p>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 mt-4">
                <div 
                  className="prose prose-sm max-w-none text-[#0D1B33]"
                  dangerouslySetInnerHTML={{ 
                    __html: (policy?.content || '').replace(/\n/g, '<br>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/•/g, '<br>•')
                  }}
                />
              </div>
            </div>

            <div className="bg-red-50 rounded-xl border border-red-200 p-4">
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <h3 className="font-medium text-red-900">Important Reminder</h3>
                  <p className="text-sm text-red-700 mt-1">
                    After submitting this form, you <strong>must call the Client Services Administrator directly by phone</strong>. 
                    Voicemails, texts, or messages relayed by others will not be accepted as proper notice.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={() => setStep(2)}
                className="bg-[#176DFF] hover:bg-[#176DFF]/90"
              >
                I Have Read the Policy
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Signature & Acknowledgment */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-6">
              <div className="flex items-center gap-3 mb-4">
                <FileSignature className="w-6 h-6 text-[#176DFF]" />
                <h2 className="text-lg font-semibold text-[#0D1B33]">
                  Policy Acknowledgment
                </h2>
              </div>
              
              <div className="bg-[#F8FAFC] rounded-lg p-4 mb-6">
                <p className="text-sm text-[#0D1B33] leading-relaxed">
                  {policy?.acknowledgment_text || 
                    'I acknowledge that I have received, read, and understood the Call-Out & No-Show Policy. I understand that failure to comply may result in disciplinary action, up to and including termination.'}
                </p>
              </div>

              {/* Acknowledgment Checkbox */}
              <label className="flex items-start gap-3 cursor-pointer mb-6 p-4 rounded-lg border-2 border-[#E2E8F0] hover:border-[#176DFF] transition-colors">
                <input
                  type="checkbox"
                  checked={policyAcknowledged}
                  onChange={(e) => setPolicyAcknowledged(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-[#CBD5E1] text-[#176DFF] focus:ring-[#176DFF]"
                />
                <div>
                  <span className="font-medium text-[#0D1B33]">
                    I have read and understand the Call-Out Policy
                  </span>
                  <p className="text-xs text-[#64748B] mt-1">
                    I understand that repeated callouts may affect my employment status
                  </p>
                </div>
              </label>

              {/* Signature */}
              <div>
                <label className="block text-sm font-medium text-[#0D1B33] mb-2">
                  Your Signature <span className="text-red-500">*</span>
                </label>
                
                {/* Signature Mode Toggle */}
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => setSignatureMode('draw')}
                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                      signatureMode === 'draw'
                        ? 'bg-[#176DFF] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Draw Signature
                  </button>
                  <button
                    onClick={() => setSignatureMode('type')}
                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                      signatureMode === 'type'
                        ? 'bg-[#176DFF] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Type Name
                  </button>
                </div>

                {/* Signature Canvas */}
                {signatureMode === 'draw' && (
                  <div className="relative mb-4">
                    <canvas
                      ref={canvasRef}
                      width={500}
                      height={150}
                      className="w-full border-2 border-dashed border-[#E2E8F0] rounded-lg bg-white cursor-crosshair touch-none"
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                    {!hasSignature && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="text-[#94A3B8]">Sign here</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Typed Signature */}
                {signatureMode === 'type' && (
                  <div className="mb-4">
                    <Input
                      type="text"
                      value={typedName}
                      onChange={(e) => setTypedName(e.target.value)}
                      placeholder="Type your full legal name"
                      className="h-12"
                    />
                    {typedName && (
                      <div className="mt-3 p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                        <p className="text-2xl italic text-center" style={{ fontFamily: '"Brush Script MT", cursive' }}>
                          {typedName}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearSignature}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              </div>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <Button
                onClick={() => setStep(3)}
                disabled={!canProceedFromStep2}
                className="bg-[#176DFF] hover:bg-[#176DFF]/90"
              >
                Continue
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Callout Details */}
        {step === 3 && !submitted && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-6">
              <div className="flex items-center gap-3 mb-6">
                <Calendar className="w-6 h-6 text-[#176DFF]" />
                <h2 className="text-lg font-semibold text-[#0D1B33]">
                  Callout Details
                </h2>
              </div>

              <div className="space-y-5">
                {/* Shift Date */}
                <div>
                  <label className="block text-sm font-medium text-[#0D1B33] mb-2">
                    Shift Date <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="date"
                    value={formData.shift_date}
                    onChange={(e) => setFormData({ ...formData, shift_date: e.target.value })}
                    min={today}
                    className="h-12"
                  />
                </div>

                {/* Shift Time (Optional) */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0D1B33] mb-2">
                      Shift Start Time
                    </label>
                    <Input
                      type="time"
                      value={formData.shift_start_time}
                      onChange={(e) => setFormData({ ...formData, shift_start_time: e.target.value })}
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0D1B33] mb-2">
                      Shift End Time
                    </label>
                    <Input
                      type="time"
                      value={formData.shift_end_time}
                      onChange={(e) => setFormData({ ...formData, shift_end_time: e.target.value })}
                      className="h-12"
                    />
                  </div>
                </div>

                {/* Client (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-[#0D1B33] mb-2">
                    Client Name (if known)
                  </label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    placeholder="Enter client name"
                    className="h-12"
                  />
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-[#0D1B33] mb-2">
                    Reason for Callout <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {CALLOUT_REASONS.map((reason) => (
                      <button
                        key={reason.value}
                        onClick={() => setFormData({ ...formData, reason: reason.value })}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          formData.reason === reason.value
                            ? 'border-[#176DFF] bg-[#176DFF]/5'
                            : 'border-[#E2E8F0] hover:border-gray-300'
                        }`}
                      >
                        <span className="font-medium text-[#0D1B33] text-sm">{reason.label}</span>
                        <p className="text-xs text-[#64748B] mt-0.5">{reason.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Reason Details */}
                <div>
                  <label className="block text-sm font-medium text-[#0D1B33] mb-2">
                    Additional Details
                  </label>
                  <textarea
                    value={formData.reason_details}
                    onChange={(e) => setFormData({ ...formData, reason_details: e.target.value })}
                    placeholder="Any additional information..."
                    className="w-full px-4 py-3 border border-[#E2E8F0] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#176DFF]/20 focus:border-[#176DFF]"
                    rows={3}
                  />
                </div>

                {/* Expected Return Date */}
                <div>
                  <label className="block text-sm font-medium text-[#0D1B33] mb-2">
                    Expected Return Date
                  </label>
                  <Input
                    type="date"
                    value={formData.expected_return_date}
                    onChange={(e) => setFormData({ ...formData, expected_return_date: e.target.value })}
                    min={formData.shift_date || today}
                    className="h-12"
                  />
                </div>
              </div>
            </div>

            {/* Final Reminder */}
            <div className="bg-red-50 rounded-xl border border-red-200 p-4">
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-red-900">Don&apos;t Forget to Call!</h3>
                  <p className="text-sm text-red-700 mt-1">
                    After submitting, you <strong>must call the Client Services Administrator</strong> to confirm your callout. 
                    This online form alone is not sufficient notice.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !formData.shift_date || !formData.reason}
                className="bg-red-600 hover:bg-red-700"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Submit Callout
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: CALL CONFIRMATION (MANDATORY) */}
        {submitted && (
          <div className="space-y-6">
            {/* Success Banner */}
            <div className="bg-emerald-50 rounded-xl border-2 border-emerald-200 p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-xl font-semibold text-emerald-900">Callout Form Submitted</h2>
              <p className="text-emerald-700 mt-2">Your callout has been recorded in the system.</p>
            </div>

            {/* CRITICAL: Phone Call Section */}
            <div className="bg-red-50 rounded-xl border-2 border-red-300 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center animate-pulse">
                  <Phone className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-red-900">YOU MUST CALL NOW</h3>
                  <p className="text-sm text-red-700">This form alone is NOT sufficient notice</p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 mb-4">
                <p className="text-sm text-[#64748B] mb-2">Client Services Administrator</p>
                <p className="text-3xl font-bold text-[#0D1B33] tracking-wide">{OFFICE_PHONE}</p>
              </div>

              <a
                href={`tel:${OFFICE_PHONE_RAW}`}
                className="w-full flex items-center justify-center gap-3 py-4 px-6 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold text-lg transition-all shadow-lg hover:shadow-xl"
                data-testid="call-office-button"
              >
                <Phone className="w-6 h-6" />
                Call {OFFICE_PHONE} Now
              </a>

              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs text-amber-800">
                  <strong>Important:</strong> Voicemails, text messages, or messages relayed through others 
                  do NOT qualify as proper notice. You must speak directly with office staff.
                </p>
              </div>
            </div>

            {/* What Happens Next */}
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-5">
              <h4 className="font-semibold text-[#0D1B33] mb-3">What Happens Next</h4>
              <ol className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#176DFF] text-white text-xs font-semibold flex items-center justify-center shrink-0">1</span>
                  <span className="text-[#64748B]">Call the office to verbally confirm your callout</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-gray-200 text-gray-600 text-xs font-semibold flex items-center justify-center shrink-0">2</span>
                  <span className="text-[#64748B]">Office staff will begin finding coverage for your shift</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-gray-200 text-gray-600 text-xs font-semibold flex items-center justify-center shrink-0">3</span>
                  <span className="text-[#64748B]">You&apos;ll be notified once coverage is confirmed</span>
                </li>
              </ol>
            </div>

            {/* Return Button (secondary, less prominent) */}
            <div className="flex justify-center pt-4">
              <Button
                variant="outline"
                onClick={() => navigate('/my-leynix')}
                className="text-[#64748B]"
              >
                Return to My Leynix
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
